// src/pages/Coupons.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FullBleed from '../components/FullBleed';
import StageFrame from '../components/StageFrame';
import CouponQrBlock from '../components/CouponQrBlock';
import bg from '../assets/card.png';
import { CouponLangSwitch, CouponLocaleProvider, useCouponLocale } from '../context/CouponLocaleContext';
import { resolveCouponName } from '../utils/couponContent';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';

axios.defaults.withCredentials = true;

function fmtDateTime(s) {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function CouponsBody() {
  const navigate = useNavigate();
  const { t, lang } = useCouponLocale();
  const usp = useMemo(() => new URLSearchParams(window.location.search), []);
  const highlightClaimId = usp.get('highlightClaimId');
  const initialTab = usp.get('tab') || 'unused';

  const TABS = useMemo(() => ([
    { key: 'unused', label: t('tabUnused') },
    { key: 'used', label: t('tabUsed') },
    { key: 'expired', label: t('tabExpired') },
  ]), [t]);

  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [successBanner, setSuccessBanner] = useState(!!highlightClaimId);
  const [qrModal, setQrModal] = useState(null);

  const load = async (status) => {
    try {
      const { data } = await axios.get('/api/member/coupons', { params: { status } });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setMsg('');
    } catch {
      setMsg(t('loginRequired'));
      setRows([]);
    }
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  useEffect(() => {
    if (!highlightClaimId) return;
    setTab('unused');
    const timer = setTimeout(() => setSuccessBanner(false), 8000);
    return () => clearTimeout(timer);
  }, [highlightClaimId]);

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px }) => (
          <>
            <style>{`
              .cp-wrap {
                position: absolute;
                left: ${px(80)}px;
                right: ${px(80)}px;
                top: ${px(200)}px;
                bottom: ${px(150)}px;
                overflow-y: auto;
                color: #111;
                background: #f0dfd4;
                border-radius: ${px(28)}px;
                padding: ${px(36)}px ${px(40)}px;
                box-shadow: 0 ${px(10)}px ${px(34)}px rgba(0,0,0,.18);
                border: ${px(3)}px solid rgba(0,0,0,.2);
              }
              .cp-title { font-size: ${px(88)}px; font-weight: 900; margin: 0 0 ${px(24)}px; }
              .cp-tabs { display: flex; gap: ${px(12)}px; margin-bottom: ${px(28)}px; flex-wrap: wrap; }
              .cp-tab {
                flex: 1; min-width: ${px(200)}px; padding: ${px(16)}px;
                border: ${px(2)}px solid #111; border-radius: ${px(14)}px;
                background: #fff; font-size: ${px(40)}px; font-weight: 800; cursor: pointer;
              }
              .cp-tab.on { background: #111; color: #fff; }
              .cp-banner {
                background: #dcfce7; border: ${px(2)}px solid #166534; color: #166534;
                padding: ${px(20)}px; border-radius: ${px(16)}px; margin-bottom: ${px(24)}px;
                font-size: ${px(42)}px; font-weight: 800;
              }
              .cp-item {
                background: #fff; border: ${px(2)}px solid #111; border-radius: ${px(22)}px;
                padding: ${px(28)}px; margin-bottom: ${px(24)}px;
              }
              .cp-item.dim { opacity: 0.55; }
              .cp-item.hl { box-shadow: 0 0 0 ${px(4)}px #166534; }
              .cp-item-name { font-size: ${px(52)}px; font-weight: 900; }
              .cp-item-meta { font-size: ${px(36)}px; opacity: 0.75; margin-top: ${px(12)}px; }
              .cp-qr-btn {
                margin-top: ${px(20)}px; width: 100%; padding: ${px(16)}px;
                font-size: ${px(42)}px; font-weight: 900; border: ${px(2)}px solid #111;
                border-radius: ${px(12)}px; background: #111; color: #fff; cursor: pointer;
              }
              .cp-modal-bg {
                position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 100;
                display: flex; align-items: center; justify-content: center; padding: ${px(24)}px;
              }
              .cp-modal {
                background: #f0dfd4; border-radius: ${px(24)}px; padding: ${px(32)}px;
                max-width: ${px(900)}px; width: 100%; text-align: center;
              }
              .cp-btn {
                position: fixed; left: ${px(140)}px; right: ${px(140)}px; bottom: ${px(60)}px;
                height: ${px(110)}px; font-size: ${px(48)}px; font-weight: 900;
                border: ${px(2)}px solid #111; border-radius: ${px(12)}px; background: #f0dfd4;
                cursor: pointer; z-index: 10;
              }
            `}</style>

            <div className="cp-wrap">
              <CouponLangSwitch px={px} />
              <h1 className="cp-title">{t('couponsTitle')}</h1>
              <div className="cp-tabs">
                {TABS.map((tabItem) => (
                  <button
                    key={tabItem.key}
                    type="button"
                    className={`cp-tab${tab === tabItem.key ? ' on' : ''}`}
                    onClick={() => setTab(tabItem.key)}
                  >
                    {tabItem.label}
                  </button>
                ))}
              </div>

              {successBanner && (
                <div className="cp-banner">{t('qrNote')}</div>
              )}
              {msg && <div style={{ color: '#b00020', fontWeight: 900, marginBottom: px(16) }}>{msg}</div>}

              {rows.length === 0 && !msg && (
                <div style={{ opacity: 0.7, fontSize: px(44), textAlign: 'center', padding: px(40) }}>
                  {t('emptyCoupons')}
                </div>
              )}

              {rows.map((r) => {
                const isHl = highlightClaimId && String(r.CLAIM_ID) === String(highlightClaimId);
                const code = resolveDisplayCode(r.CLAIM_ID, r.DISPLAY_CODE);
                return (
                  <div
                    key={String(r.CLAIM_ID)}
                    className={`cp-item${tab !== 'unused' ? ' dim' : ''}${isHl ? ' hl' : ''}`}
                  >
                    <div className="cp-item-name">{resolveCouponName(r, lang)}</div>
                    <div className="cp-item-meta">{t('expireLabel')} {r.END_AT || '—'}</div>
                    {r.USED_AT && (
                      <div className="cp-item-meta">{fmtDateTime(r.USED_AT)}</div>
                    )}
                    {tab === 'unused' && r.REDEEM_TOKEN && (
                      <>
                        <div className="cp-item-meta">{t('redeemCode')} {code}</div>
                        <button
                          type="button"
                          className="cp-qr-btn"
                          onClick={() => setQrModal(r)}
                        >
                          {t('showQr')}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {qrModal && (
              <div className="cp-modal-bg" onClick={() => setQrModal(null)} role="presentation">
                <div className="cp-modal" onClick={(e) => e.stopPropagation()} role="dialog">
                  <div style={{ fontSize: px(48), fontWeight: 900, marginBottom: px(16) }}>
                    {resolveCouponName(qrModal, lang)}
                  </div>
                  <CouponQrBlock
                    redeemToken={qrModal.REDEEM_TOKEN}
                    claimId={qrModal.CLAIM_ID}
                    displayCode={resolveDisplayCode(qrModal.CLAIM_ID, qrModal.DISPLAY_CODE)}
                    px={px}
                    size={Math.round(px(400))}
                    label={t('redeemCode')}
                  />
                  <p style={{ fontSize: px(36), marginTop: px(16) }}>{t('qrNote')}</p>
                  <button type="button" className="cp-qr-btn" onClick={() => setQrModal(null)}>OK</button>
                </div>
              </div>
            )}

            <button type="button" className="cp-btn" onClick={() => navigate('/card')}>
              {t('goCard')}
            </button>
          </>
        )}
      />
    </FullBleed>
  );
}

export default function Coupons() {
  return (
    <CouponLocaleProvider>
      <CouponsBody />
    </CouponLocaleProvider>
  );
}
